/**
 * Product Tab Navigation
 * Simple in-page tab bar for PDP section navigation.
 * Scroll highlight and smooth-scroll on click.
 * The sticky bar with price+ATC is handled by sticky-add-to-cart.
 */
class ProductTabNav extends HTMLElement {
  /** @type {IntersectionObserver|null} */
  #scrollHighlightObserver = null;

  connectedCallback() {
    this.#hideMissingTabs();
    this.#setupTabClicks();
    this.#setupScrollHighlight();
  }

  disconnectedCallback() {
    this.#scrollHighlightObserver?.disconnect();
  }

  // ── Hide tabs whose target section is absent or not rendered ──
  #hideMissingTabs() {
    this.querySelectorAll('.pdp-tab').forEach((tab) => {
      const target = tab.dataset.target;
      if (!target) return;
      const section = document.querySelector(`[data-pdp-section="${target}"]`);
      if (!section || section.offsetParent === null) {
        tab.hidden = true;
      }
    });
  }

  // ── Smooth scroll to section on tab click ──
  #setupTabClicks() {
    this.querySelectorAll('.pdp-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.target;
        if (!target) return;
        const section = document.querySelector(`[data-pdp-section="${target}"]`);
        if (!section) return;
        const offset = this.getBoundingClientRect().height + 8;
        const top = section.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        this.#setActive(tab);
      });
    });
  }

  // ── Highlight active tab based on scroll position ──
  // Runs on all breakpoints: the mobile sticky tab bar (sticky-add-to-cart.js)
  // mirrors this active state and scrolls its pill into view.
  #setupScrollHighlight() {
    const tabs = [...this.querySelectorAll('.pdp-tab:not([hidden])')];
    if (!tabs.length) return;

    this.#scrollHighlightObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = entry.target.dataset.pdpSection;
          const tab = this.querySelector(`.pdp-tab[data-target="${name}"]`);
          if (tab) this.#setActive(tab);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );

    tabs.forEach((tab) => {
      const section = document.querySelector(`[data-pdp-section="${tab.dataset.target}"]`);
      if (section) this.#scrollHighlightObserver.observe(section);
    });
  }

  #setActive(activeTab) {
    this.querySelectorAll('.pdp-tab').forEach((t) => t.classList.remove('pdp-tab--active'));
    activeTab.classList.add('pdp-tab--active');
    this.#scrollActiveTabIntoView(activeTab);
  }

  // ── Scroll the active tab into view within the horizontal pill container ──
  #scrollActiveTabIntoView(tab) {
    const wrap = this.querySelector('.pdp-tab-nav__tabs-wrap') ?? this.querySelector('.pdp-tab-nav__tabs');
    if (!wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const scrollLeft = wrap.scrollLeft + (tabRect.left - wrapRect.left) - 16;
    wrap.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }
}

if (!customElements.get('product-tab-nav')) {
  customElements.define('product-tab-nav', ProductTabNav);
}


